import { useEffect, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import {
    confirmWhatsappAppointment,
    getWhatsappAppointmentConfirmation
} from '../../../../services/PublicBookingService';
import './PublicBooking.css';
import PublicBrandSignature from '../../../components/Common/PublicBrandSignature';
import ImageWithSkeleton from '../../../components/Custom/Common/ImageWithSkeleton';

const resolvePublicAssetUrl = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `${process.env.REACT_APP_API_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

const formatBranchAddress = (address) => {
    if (!address) return '';
    // Os grupos evitam vírgulas e separadores soltos quando o cadastro da filial está incompleto.
    const street = [address.street, address.number].filter(Boolean).join(', ');
    const area = [address.neighborhood, address.city, address.state].filter(Boolean).join(' - ');
    return [street, address.complement, area, address.zipCode ? `CEP ${address.zipCode}` : '']
        .filter(Boolean)
        .join(' • ');
};

const AppointmentConfirmationPage = () => {
    const { token } = useParams();
    const [appointment, setAppointment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        getWhatsappAppointmentConfirmation(token)
            .then((response) => setAppointment(response.data))
            .catch((requestError) => setError(requestError.response?.data?.message || 'Link de confirmação inválido.'))
            .finally(() => setLoading(false));
    }, [token]);

    const confirm = () => {
        setConfirming(true);
        setError('');
        confirmWhatsappAppointment(token)
            .then((response) => setAppointment(response.data))
            .catch((requestError) => setError(requestError.response?.data?.message || 'Não foi possível confirmar o agendamento.'))
            .finally(() => setConfirming(false));
    };

    const confirmed = appointment?.confirmedAt || appointment?.status === 'CONFIRMED';
    // Somente sobrescrevemos a variável do tema quando a empresa possui uma cor válida;
    // sem identidade cadastrada, o CSS preserva a cor principal padrão do Colmeia.
    const brandColor = /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(appointment?.companyHeaderColor || '')
        ? appointment.companyHeaderColor
        : null;
    const logoUrl = resolvePublicAssetUrl(appointment?.companyLogoUrl);
    const scheduledDate = appointment?.scheduledDate ? moment(appointment.scheduledDate) : null;
    const branchAddress = formatBranchAddress(appointment?.branchAddress);

    return (
        <main
            className="appointment-confirmation-page"
            style={brandColor ? { '--confirmation-brand': brandColor } : undefined}
        >
            <div className="appointment-confirmation-backdrop" aria-hidden="true"></div>
            <section className="appointment-confirmation-shell">
                <header className="appointment-confirmation-brand">
                    {logoUrl ? (
                        <div className="appointment-confirmation-logo">
                            <ImageWithSkeleton
                                src={logoUrl}
                                alt={`Logo ${appointment?.companyName || 'da empresa'}`}
                                fill
                            />
                        </div>
                    ) : (
                        <div className="appointment-confirmation-logo appointment-confirmation-logo--fallback" aria-hidden="true">
                            <i className="fa-solid fa-calendar-check"></i>
                        </div>
                    )}
                    <div>
                        <span>Agendamento online</span>
                        <h1>{appointment?.companyName || 'Confirmação de agendamento'}</h1>
                    </div>
                </header>

                <div className="appointment-confirmation-card">
                    {loading ? (
                        <div className="appointment-confirmation-state">
                            <Spinner animation="border" />
                            <p>Carregando os dados do seu agendamento...</p>
                        </div>
                    ) : error ? (
                        <div className="appointment-confirmation-state">
                            <div className="appointment-confirmation-status appointment-confirmation-status--error">
                                <i className="fa-solid fa-link-slash"></i>
                            </div>
                            <h2>Não foi possível abrir este agendamento</h2>
                            <Alert variant="danger" className="mb-0">{error}</Alert>
                        </div>
                    ) : (
                        <>
                            <div className="appointment-confirmation-heading">
                                <div className={`appointment-confirmation-status ${confirmed ? 'is-confirmed' : ''}`}>
                                    <i className={`fa-solid ${confirmed ? 'fa-circle-check' : 'fa-calendar-day'}`}></i>
                                </div>
                                <span className="appointment-confirmation-eyebrow">
                                    {confirmed ? 'Tudo certo' : 'Só falta confirmar'}
                                </span>
                                <h2>{confirmed ? 'Agendamento confirmado!' : `Olá, ${appointment.customerName || 'cliente'}!`}</h2>
                                <p>
                                    {confirmed
                                        ? 'Sua confirmação foi registrada. Esperamos você no horário combinado.'
                                        : 'Confira os detalhes e confirme sua presença no botão abaixo.'}
                                </p>
                            </div>

                            <div className="appointment-confirmation-date">
                                <div className="appointment-confirmation-date__day">
                                    <span>{scheduledDate?.format('DD')}</span>
                                    {scheduledDate?.format('MMM')}
                                </div>
                                <div>
                                    <span>Data e horário</span>
                                    <strong>{scheduledDate?.format('dddd, DD [de] MMMM')}</strong>
                                    <small><i className="fa-regular fa-clock"></i> {scheduledDate?.format('HH:mm')}</small>
                                </div>
                            </div>

                            <div className="appointment-confirmation-details">
                                {/* Não exibimos um valor genérico enquanto versões antigas da API ainda estiverem em execução. */}
                                {appointment.companyName && (
                                    <div>
                                        <i className="fa-regular fa-building"></i>
                                        <span>Empresa<strong>{appointment.companyName}</strong></span>
                                    </div>
                                )}
                                <div>
                                    <i className="fa-solid fa-scissors"></i>
                                    <span>Serviço<strong>{appointment.serviceName || 'Não informado'}</strong></span>
                                </div>
                                <div>
                                    <i className="fa-regular fa-user"></i>
                                    <span>Profissional<strong>{appointment.employeeName || 'Não informado'}</strong></span>
                                </div>
                                <div>
                                    <i className="fa-solid fa-location-dot"></i>
                                    <span>
                                        Local
                                        <strong>{appointment.branchName || 'Não informado'}</strong>
                                        {branchAddress && <small>{branchAddress}</small>}
                                    </span>
                                </div>
                            </div>

                            {confirmed ? (
                                <div className="appointment-confirmation-success">
                                    <i className="fa-brands fa-whatsapp"></i>
                                    <span>
                                        <strong>Presença confirmada via WhatsApp</strong>
                                        Você já pode fechar esta página.
                                    </span>
                                </div>
                            ) : (
                                <Button
                                    className="appointment-confirmation-button"
                                    size="lg"
                                    onClick={confirm}
                                    disabled={confirming}
                                >
                                    {confirming ? (
                                        <><Spinner animation="border" size="sm" className="me-2" />Confirmando...</>
                                    ) : (
                                        <><i className="fa-solid fa-check me-2"></i>Confirmar minha presença</>
                                    )}
                                </Button>
                            )}
                        </>
                    )}
                </div>

                <footer className="appointment-confirmation-footer">
                    <i className="fa-solid fa-shield-halved"></i>
                    Confirmação segura e exclusiva para este agendamento
                </footer>

                <PublicBrandSignature className="is-compact" />
            </section>
        </main>
    );
};

export default AppointmentConfirmationPage;
